import { Box, Button, CircularProgress, LinearProgress, linearProgressClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import { addQueryEngagementMetric } from 'api/metrics'
import { completeQuery, getQueryById } from 'api/query'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import { minButtonWidth } from 'const'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from 'react-query'
import {
  ArticleType,
  IMetricScope,
  IMetricType,
  Query,
  QueryAnsweredQuestion,
  QueryResult,
} from 'types'
import { formatMetricDate, getRoute } from 'utils'
import { routes } from 'routes/routes'
import QuestionScreen from '../question-screen'
import QuestionnaireFooter from '../QuestionnaireFooter'
import FirstScreenQuestionnaire from './intro'
import { QueryMetric } from './QueryMetric'
import FinalScreen from './summary'

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 16,
  borderRadius: 8,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '005B8C',
  },
}))

interface Props {
  id: number
  handleClose: () => void
  onLoadingStart: () => void
  onLoadingFail: () => void
  onLoadingFinish: () => void
  onQuizTaken?: () => void
}

const getProgress = (stage: number, max: number) => {
  if (stage === FIRST_SCREEN) {
    return 0
  }
  if (stage === LAST_SCREEN) {
    return 100
  }
  return ((stage + 1) / (max + 2)) * 100
}

const FIRST_SCREEN = -1
const LAST_SCREEN = -2

const ModalContent = (props: Props) => {
  const { handleClose } = props
  const { user } = useAuthContext()

  const [stage, setStage] = useState(FIRST_SCREEN)
  const [query, setQuery] = useState<Query>()
  const [answers, setAnswers] = useState<Record<string, QueryAnsweredQuestion>>({})
  const [queryResult, setQueryResult] = useState<Partial<QueryResult>>({})

  const navigate = useNavigate()

  const handleArticleClick = useCallback(
    (type: ArticleType, id: number) => {
      const routeTemplate =
        type === ArticleType.CareArticle ? routes.journeyItem : routes.learnLibraryItem
      const paramPrefix = type === ArticleType.CareArticle ? 'journeyItem' : 'learnLibraryItem'
      const route = getRoute(routeTemplate, {
        [`${paramPrefix}Type`]: type,
        [`${paramPrefix}Id`]: id,
      })
      navigate(route)
      handleClose()
    },
    [navigate, handleClose],
  )

  const saveResults = useMutation(completeQuery, {
    onSuccess: ({ data }) => {
      setQueryResult(data)
      NotificationSys.showSuccess(
        'Your results for this query are saved in your Query list in Learn Library',
      )
      props.onQuizTaken && props.onQuizTaken()
    },
  })

  useEffect(() => {
    props.onLoadingStart()
    getQueryById({ id: props.id })
      .then((r) => {
        setQuery(r.data)
        props.onLoadingFinish()
      })
      .catch(() => props.onLoadingFail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const finishQuiz = () => {
    return saveResults.mutateAsync({
      answers: Object.values(answers),
      id: props.id,
    })
  }

  const question = useMemo(() => {
    return query?.questions[stage]
  }, [query, stage])

  const questionAnswered = useCallback(
    (answer: string) => {
      if (!question) {
        return
      }

      const result = { ...answers }
      result[question.id] = {
        answer,
        questionId: question.id,
      }
      setAnswers(result)
    },
    [answers, question],
  )

  const maxProgress = useMemo(() => {
    if (!query) {
      return -1
    }
    return query.questions.length - 1
  }, [query])

  const answer = useMemo(() => {
    if (question) {
      return answers[question?.id]
    }
  }, [answers, question])

  const stageType = useMemo(() => {
    if (stage == FIRST_SCREEN) {
      return 'intro'
    }

    if (stage == LAST_SCREEN) {
      return 'summary'
    }

    return 'question'
  }, [stage])

  const onClickNext = async () => {
    if (stage === LAST_SCREEN) {
      handleClose()
    } else if (stage === maxProgress) {
      await finishQuiz()
      setStage(LAST_SCREEN)
    } else {
      setStage(stage + 1)
    }
  }

  const onClickPrevious = () => {
    if (stage === LAST_SCREEN) {
      setStage(maxProgress)
    } else {
      setStage(stage - 1)
    }
  }

  const isPreviousVisible = stage !== FIRST_SCREEN && stage !== LAST_SCREEN

  const retakeQuiz = () => {
    setStage(FIRST_SCREEN)
    setAnswers({})
  }

  const handleSaveAudioMetric = async (value: number, _uuid: string, scope: IMetricScope) => {
    if (!user || !query) {
      return
    }

    addQueryEngagementMetric({
      userId: user.id,
      queryId: query.id,
      learnArticleId: query.learnArticleId,
      scope,
      engagedFrom: formatMetricDate(value),
      engagedTo: formatMetricDate(),
    })
  }

  if (!query) {
    return null
  }

  const renderStage = () => {
    if (stageType == 'intro')
      return (
        <FirstScreenQuestionnaire handleClose={handleClose} intro={query.intro} name={query.name} />
      )

    if (stageType == 'summary')
      return (
        <FinalScreen
          retakeQuiz={retakeQuiz}
          handleClose={handleClose}
          name={query.name}
          notificationText={queryResult.notificationText}
          learnArticleId={queryResult.learnArticleId}
          learnArticleName={queryResult.learnArticleName}
          careArticleId={queryResult.careArticleId}
          careArticleName={queryResult.careArticleName}
          onArticleClick={handleArticleClick}
        />
      )

    if (!question) {
      return null
    }

    return (
      <QuestionScreen
        key={question.id}
        answer={answer?.answer}
        onChange={questionAnswered}
        metricType={IMetricType.learnArticle}
        metricEntityId={query.id}
        onSaveAudioMetric={handleSaveAudioMetric}
        handleClose={handleClose}
        question={question}
      />
    )
  }

  return (
    <>
      <Box sx={{ mb: 4, flex: 1 }}>{renderStage()}</Box>
      <QueryMetric query={query} />
      <Box>
        {stage !== LAST_SCREEN && (
          <BorderLinearProgress variant="determinate" value={getProgress(stage, maxProgress)} />
        )}

        <QuestionnaireFooter
          leftButton={
            <Button variant="outlined" onClick={onClickPrevious} sx={minButtonWidth}>
              {'< Previous'}
            </Button>
          }
          rightButton={
            <NextButton
              lastScreen={stage == LAST_SCREEN}
              onClickNext={onClickNext}
              saving={saveResults.isLoading}
              disabled={stageType == 'question' && !answer?.answer}
            />
          }
          isPreviousVisible={isPreviousVisible}
          isNextVisible={true}
        />
      </Box>
    </>
  )
}

type NextButtonProps = {
  onClickNext: () => void
  saving: boolean
  lastScreen: boolean
  disabled: boolean
}

const NextButton = ({ saving, onClickNext, lastScreen, disabled }: NextButtonProps) => {
  if (lastScreen) {
    return null
  }

  return (
    <Button
      variant="outlined"
      onClick={onClickNext}
      sx={minButtonWidth}
      disabled={saving || disabled}
    >
      {!saving ? 'Next >' : null}
      {saving ? <CircularProgress size="1rem" /> : null}
    </Button>
  )
}

export default ModalContent
